import React, { useEffect, useState } from 'react';
import {
  BoxArrowUpRight,
  BriefcaseFill,
  CalendarEvent,
  CameraVideoFill,
  CheckCircleFill,
  Clipboard,
  EnvelopeFill,
  FileEarmarkTextFill,
  InfoCircle,
  Link45deg,
  Linkedin,
  SendFill,
  ShieldFill,
  Stars,
  Whatsapp,
  X,
} from 'react-bootstrap-icons';
import { resolveProfileImageUrl } from '../../utils/userProfile';
import {
  buildShareInvitationMessage,
  buildShareInvitationText,
  linkedInShareUrl,
  mailtoShareUrl,
  openShareWindow,
  whatsAppShareUrl,
} from './shareLinks';
import './ShareProfileModal.css';

export interface ShareProfileCandidateInfo {
  displayName?: string;
  jobTitle?: string;
  profileImg?: string;
  profileCompletePercent?: number | null;
  hasPortfolio?: boolean;
  hasVideoResponses?: boolean;
  hasResume?: boolean;
  hasSkills?: boolean;
  hasAvailability?: boolean;
}

export interface ShareProfileModalProps {
  show: boolean;
  onHide: () => void;
  profileLink: string;
  recruiterEmail: string;
  onRecruiterEmailChange: (value: string) => void;
  onCopyLink: () => void | Promise<void>;
  onSendEmail: () => void | Promise<void>;
  sending?: boolean;
  sendSuccess?: boolean;
  sendError?: boolean;
  previewHref?: string;
  durationDays?: number;
  onDurationDaysChange?: (days: number) => void;
  candidate?: ShareProfileCandidateInfo;
}

const SHARE_DURATION_OPTIONS = [30, 60, 90, 120] as const;

const isValidEmail = (email: string) => /^\S+@\S+\.\S+$/.test(email.trim());

const RECRUITER_VIEW_ITEMS = [
  { key: 'portfolio', label: 'Portfolio', icon: BriefcaseFill },
  { key: 'videos', label: 'Video responses', icon: CameraVideoFill },
  { key: 'skills', label: 'Skills', icon: Stars },
  { key: 'resume', label: 'Resume', icon: FileEarmarkTextFill },
  { key: 'calendar', label: 'Availability', icon: CalendarEvent },
] as const;

const ShareProfileModal: React.FC<ShareProfileModalProps> = ({
  show,
  onHide,
  profileLink,
  recruiterEmail,
  onRecruiterEmailChange,
  onCopyLink,
  onSendEmail,
  sending = false,
  sendSuccess = false,
  sendError = false,
  previewHref,
  durationDays = 30,
  onDurationDaysChange,
  candidate,
}) => {
  const [copyFeedback, setCopyFeedback] = useState<'idle' | 'success' | 'error'>('idle');
  const [emailTouched, setEmailTouched] = useState(false);

  useEffect(() => {
    if (show) {
      setCopyFeedback('idle');
      setEmailTouched(false);
    }
  }, [show]);

  useEffect(() => {
    if (!show) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [show]);

  const handleCopy = async () => {
    try {
      await onCopyLink();
      setCopyFeedback('success');
    } catch {
      setCopyFeedback('error');
    }
  };

  const trimmedEmail = recruiterEmail.trim();
  const emailInvalid = emailTouched && trimmedEmail && !isValidEmail(trimmedEmail);

  const displayName = candidate?.displayName?.trim() || 'Your profile';
  const jobTitle = candidate?.jobTitle?.trim() || 'Professional';
  const profileImg = resolveProfileImageUrl(candidate?.profileImg);
  const completion =
    typeof candidate?.profileCompletePercent === 'number'
      ? Math.max(0, Math.min(100, Math.round(candidate.profileCompletePercent)))
      : null;

  const visibilityFlags: Record<string, boolean> = {
    portfolio: candidate?.hasPortfolio !== false,
    videos: candidate?.hasVideoResponses !== false,
    skills: candidate?.hasSkills !== false,
    resume: candidate?.hasResume !== false,
    calendar: candidate?.hasAvailability !== false,
  };

  const shareName = displayName !== 'Your profile' ? displayName : 'I';
  const shareTitle = jobTitle !== 'Professional' ? jobTitle : undefined;

  const invitationMessage = profileLink
    ? buildShareInvitationMessage({
        displayName: shareName,
        jobTitle: shareTitle,
        profileUrl: profileLink,
      })
    : '';

  const linkedInMessage = profileLink
    ? buildShareInvitationText({
        displayName: shareName,
        jobTitle: shareTitle,
      })
    : '';

  const shareViaLinkedIn = () => {
    if (!profileLink || !linkedInMessage) return;
    openShareWindow(linkedInShareUrl(profileLink, linkedInMessage));
  };

  const shareViaWhatsApp = () => {
    if (!invitationMessage) return;
    openShareWindow(whatsAppShareUrl(invitationMessage));
  };

  const shareViaMailto = () => {
    if (!invitationMessage) return;
    const url = mailtoShareUrl(invitationMessage, {
      recruiterEmail: trimmedEmail || undefined,
      candidateName: displayName !== 'Your profile' ? displayName : undefined,
    });
    window.location.href = url;
  };

  const handleSendInvitation = () => {
    setEmailTouched(true);
    if (!trimmedEmail || !isValidEmail(trimmedEmail)) return;
    void onSendEmail();
  };

  if (!show) return null;

  return (
    <div
      className="share-overlay"
      role="presentation"
      onClick={onHide}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onHide();
      }}
    >
      <div
        className="share-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-profile-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="share-header">
          <h3 id="share-profile-title">Share your profile</h3>
          <button type="button" className="icon-close" onClick={onHide} aria-label="Close">
            <X size={24} aria-hidden />
          </button>
        </div>

        <div className="profile-summary">
          {profileImg ? (
            <img src={profileImg} alt={displayName} />
          ) : (
            <div className="profile-avatar-placeholder" aria-hidden>
              {displayName.charAt(0).toUpperCase()}
            </div>
          )}

          <div className="profile-summary__details">
            <h4>{displayName}</h4>
            <p className="profile-summary__role">{jobTitle}</p>

            {completion !== null && completion >= 80 ? (
              <span className="complete-badge">
                <CheckCircleFill size={13} aria-hidden />
                {completion}% Profile Complete
              </span>
            ) : null}

            <div className="can-view">
              <small>Recruiters can view:</small>
              <div className="view-items">
                {RECRUITER_VIEW_ITEMS.map(({ key, label, icon: Icon }) => (
                  <span key={key} className={visibilityFlags[key] ? '' : 'is-muted'}>
                    <Icon size={14} aria-hidden />
                    {label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <hr />

        <section className="share-section--link">
          <h5>Share link</h5>
          <p className="share-section-desc">Anyone with this link can view your public profile.</p>

          <div className="copy-row">
            <div className="link-box">
              <Link45deg size={18} className="link-box__icon" aria-hidden />
              <span title={profileLink}>{profileLink}</span>
            </div>

            <button type="button" className="outline-green" onClick={() => void handleCopy()}>
              <Clipboard size={13} aria-hidden />
              Copy Link
            </button>
          </div>

          {copyFeedback === 'success' && (
            <div className="share-feedback share-feedback--success">Link copied to clipboard.</div>
          )}
          {copyFeedback === 'error' && (
            <div className="share-feedback share-feedback--error">
              Unable to copy. Please copy manually.
            </div>
          )}

          {previewHref ? (
            <a
              href={previewHref}
              className="preview-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              Preview public profile
              <BoxArrowUpRight size={14} aria-hidden />
            </a>
          ) : null}
        </section>

        <div className="share-section-separator" role="presentation" aria-hidden="true" />

        <section className="share-section--invite">
          <h5>Invite recruiter</h5>
          <p className="share-section-desc">Send your profile directly to recruiters via email.</p>

          <div className="share-invite-fields">
            <div className="share-invite-field">
              <label className="share-field-label" htmlFor="share-recruiter-email">
                Recruiter email
              </label>
              <div className="input-icon">
                <EnvelopeFill size={16} aria-hidden />
                <input
                  id="share-recruiter-email"
                  type="email"
                  value={recruiterEmail}
                  onChange={(event) => onRecruiterEmailChange(event.target.value)}
                  onBlur={() => setEmailTouched(true)}
                  placeholder="recruiter@company.com"
                />
              </div>
            </div>

            <div className="share-invite-field">
              <label
                className="share-field-label share-field-label--with-info"
                htmlFor="share-access-duration"
              >
                Access duration
                <InfoCircle
                  size={11}
                  className="share-field-label__info"
                  title="Recruiters receive instant Full Access for this period."
                />
              </label>
              <div className="input-icon">
                <CalendarEvent size={16} aria-hidden />
                <select
                  id="share-access-duration"
                  value={durationDays}
                  onChange={(event) => onDurationDaysChange?.(Number(event.target.value))}
                >
                  {SHARE_DURATION_OPTIONS.map((days) => (
                    <option key={days} value={days}>
                      {days} days{days === 30 ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {emailInvalid && (
            <div className="share-feedback share-feedback--error">
              Enter a valid recruiter email address.
            </div>
          )}
          {sendError && (
            <div className="share-feedback share-feedback--error">
              Unable to send invitation right now. Please try again.
            </div>
          )}
          {sendSuccess && (
            <div className="share-feedback share-feedback--success">
              Invitation sent successfully.
            </div>
          )}

          <div className="notice">
            <ShieldFill size={14} aria-hidden />
            <span>They&apos;ll get instant access for the selected duration.</span>
          </div>
        </section>

        <div className="share-footer">
          <div className="share-footer__quick-actions">
            <button
              type="button"
              className="quick-btn quick-btn--copy"
              onClick={() => void handleCopy()}
              title="Copy link"
            >
              <Link45deg size={13} aria-hidden />
              <span>Copy Link</span>
            </button>

            <button
              type="button"
              className="quick-btn quick-btn--email"
              onClick={shareViaMailto}
              title="Share via email"
            >
              <EnvelopeFill size={13} aria-hidden />
              <span>Email</span>
            </button>

            <button
              type="button"
              className="quick-btn quick-btn--linkedin"
              onClick={shareViaLinkedIn}
              title="Share on LinkedIn"
            >
              <Linkedin size={13} aria-hidden />
              <span>LinkedIn</span>
            </button>

            <button
              type="button"
              className="quick-btn quick-btn--whatsapp"
              onClick={shareViaWhatsApp}
              title="Share on WhatsApp"
            >
              <Whatsapp size={13} aria-hidden />
              <span>WhatsApp</span>
            </button>
          </div>

          <button
            type="button"
            className="send-btn"
            onClick={handleSendInvitation}
            disabled={!trimmedEmail || sending}
          >
            <SendFill size={12} aria-hidden />
            {sending ? 'Sending...' : 'Send Invitation'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareProfileModal;
